import { LoaderCircle, RadioTower, WifiOff } from "lucide-react";

/**
 * Small inline pill summarizing a team's live-publisher state.
 *
 * Backed by aggregate endpoints (`/api/live/team-statuses` and
 * `/api/admin/live/team-statuses`) so the public `/teams` grid and
 * `/admin/teams` list can render Live/Offline without one poll per
 * team. Visual tokens match the existing badge style on `TeamCard`.
 */
export type LiveStatusPillState =
  | "live"
  | "offline"
  | "connecting"
  | "unknown";

export type LiveStatusPillProps = {
  state: LiveStatusPillState;
  className?: string;
};

/** Derive a pill state from an aggregate-response row.
 *
 * - ``unknown``: MediaMTX is unreachable — status genuinely unknown.
 * - ``offline``: no publisher attempting to connect.
 * - ``connecting``: publisher is attached but hasn't emitted segments
 *   yet (transient; usually <2s). Distinct from ``unknown`` so the
 *   pill tooltip can name what's actually happening.
 * - ``live``: publisher is emitting segments.
 */
export function deriveLiveStatusPillState(
  reachable: boolean | undefined,
  entry: { active: boolean; ready: boolean } | undefined,
): LiveStatusPillState {
  if (reachable === false) return "unknown";
  if (!entry || !entry.active) return "offline";
  if (entry.ready) return "live";
  return "connecting";
}

const STATE_LABEL: Record<LiveStatusPillState, string> = {
  live: "Live",
  offline: "Not live",
  connecting: "Connecting",
  unknown: "Unavailable",
};

const STATE_TITLE: Record<LiveStatusPillState, string> = {
  live: "A publisher is currently streaming",
  offline: "No active publisher",
  connecting: "Publisher connected, waiting for the first segment",
  unknown: "Status unavailable",
};

const STATE_CLASS: Record<LiveStatusPillState, string> = {
  live: "border-accent bg-accent text-accent-ink",
  offline: "border-destructive bg-destructive text-paper",
  connecting: "border-rule text-ink-muted",
  unknown: "border-rule text-ink-muted",
};

function StateIcon({ state }: { state: LiveStatusPillState }) {
  if (state === "live") return <RadioTower size={13} aria-hidden="true" />;
  if (state === "connecting") {
    return (
      <LoaderCircle
        size={13}
        aria-hidden="true"
        className="animate-spin"
      />
    );
  }
  return <WifiOff size={13} aria-hidden="true" />;
}

export function LiveStatusPill({ state, className }: LiveStatusPillProps) {
  const base =
    "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[0.68rem] font-semibold leading-none";
  return (
    <span
      data-testid="live-status-pill"
      data-state={state}
      title={STATE_TITLE[state]}
      className={`${base} ${STATE_CLASS[state]}${className ? ` ${className}` : ""}`}
    >
      <StateIcon state={state} />
      {STATE_LABEL[state]}
    </span>
  );
}
